import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { fetchApi } from "./api";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    let msg = `${res.status}: ${text}`;
    try {
      const j = JSON.parse(text);
      if (j.error) msg = j.error + (j.hint ? ` (${j.hint})` : "");
      if (j.requestId) msg += ` [${j.requestId}]`;
    } catch {
      /* plain text */
    }
    throw new Error(msg);
  }
}

function authAndWalletHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  try {
    const w = JSON.parse(localStorage.getItem("poker_grudge_wallet") || "{}");
    if (w.publicKey) headers["X-Wallet-Address"] = w.publicKey;
  } catch {
    /* ignore */
  }
  try {
    const token =
      localStorage.getItem("grudge_auth_token") ||
      localStorage.getItem("grudge_session_token");
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  return headers;
}

/** Supports both (url, method, data) legacy and (method, url, data) orders. */
export async function apiRequest(
  methodOrUrl: string,
  urlOrMethod?: string,
  data?: unknown | undefined,
): Promise<Response> {
  let method = "GET";
  let url = methodOrUrl;
  if (
    urlOrMethod &&
    ["GET", "POST", "PUT", "PATCH", "DELETE"].includes(methodOrUrl.toUpperCase())
  ) {
    method = methodOrUrl.toUpperCase();
    url = urlOrMethod;
  } else if (urlOrMethod) {
    method = urlOrMethod.toUpperCase();
    url = methodOrUrl;
  }

  const headers = authAndWalletHeaders(
    data ? { "Content-Type": "application/json" } : undefined,
  );
  // Pass through Idempotency-Key when body includes it (table join, etc.)
  if (
    data &&
    typeof data === "object" &&
    data !== null &&
    "idempotencyKey" in data &&
    typeof (data as { idempotencyKey?: string }).idempotencyKey === "string"
  ) {
    headers["Idempotency-Key"] = (
      data as { idempotencyKey: string }
    ).idempotencyKey;
  }

  const res = await fetchApi(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const path = queryKey.join("/") as string;
    const res = await fetchApi(path.startsWith("/") ? path : `/${path}`, {
      headers: authAndWalletHeaders(),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      gcTime: 300_000,
      retry: (count, err) => {
        const msg = err instanceof Error ? err.message : "";
        if (msg.includes("401") || msg.includes("403")) return false;
        return count < 2;
      },
      retryDelay: (i) => Math.min(1000 * 2 ** i, 4000),
    },
    mutations: {
      retry: 0,
    },
  },
});
